import { timestampToDate } from "@/utils/proto-utils";
import { formatInt, JsonType } from "@/utils/string-utils";
import { isMessage } from "@bufbuild/protobuf";
import { TimestampSchema } from "@bufbuild/protobuf/wkt";

/**
 * Displays a byte array as a mixture of ascii characters
 * (escaped when possible) and hex elements
 *
 * @param input A byte array
 * @returns a string representation of the input, escaping ascii characters when possible
 */
export const prettyFormatByteArray = (input: Uint8Array): string => {
  let result = "";

  for (const byte of input) {
    // printable ASCII range
    if (byte >= 32 && byte <= 126) {
      result += String.fromCharCode(byte);
    } else {
      result += "\\x" + byte.toString(16).padStart(2, "0");
    }
  }

  return result;
};

/**
 * Hack for json objects that have already been stringified before going into BQ.
 *
 * The `":` check is a cheap pre-filter for "looks like a stringified object";
 * the parse attempt guards against malformed payloads (e.g. audit logs written
 * by an old server bug that embedded nested objects without escaping their
 * quotes), so callers can safely JSON.parse when this returns true.
 */
export const stringIsStringifiedJson = (val: string): boolean => {
  if (!val.includes('":')) {
    return false;
  }
  try {
    JSON.parse(val);
    return true;
  } catch {
    return false;
  }
};

export const prettyFormatJsonString = (maybeJson: string): string => {
  try {
    return JSON.stringify(JSON.parse(maybeJson), null, 2);
  } catch {
    return maybeJson;
  }
};

export const jsonReplacerStripFalsy = (_key: string, value: JsonType): JsonType | undefined => {
  return value || undefined;
};

export const jsonReplacerStripEmptyString = (
  _key: string,
  value: JsonType,
): JsonType | undefined => {
  return value !== "" ? value : undefined;
};

export const jsonReplacerSafeBigInt = (_key: string, value: JsonType): JsonType | undefined => {
  if (typeof value !== "bigint") {
    return value;
  }
  try {
    return Number(value);
  } catch {
    return value;
  }
};

// Lossless BigInt: stringifies the value. Use when precision matters
// (e.g. cache keys, large IDs round-tripped through a URL).
export const jsonReplacerBigIntToString = (_key: string, value: JsonType): JsonType | undefined => {
  return typeof value === "bigint" ? value.toString() : value;
};

// Streaming/parquet payloads are typically UTF-8 (JSON or text). Decode
// strictly so non-UTF-8 input (e.g. protobuf) falls through to base64 with a
// prefix instead of rendering as mojibake. Without a helper like this,
// JSON.stringify serializes Uint8Array as `{"0":..., "1":...}` (one entry per
// byte index).
export const bytesToReadableString = (bytes: Uint8Array): string => {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return `base64:${btoa(binary)}`;
  }
};

// Replacer for JSON.stringify suitable for file downloads: handles BigInt
// (via jsonReplacerSafeBigInt) and Uint8Array (via bytesToReadableString).
export const jsonReplacerForDownload = (
  key: string,
  value: JsonType | Uint8Array,
): JsonType | undefined => {
  if (value instanceof Uint8Array) {
    return bytesToReadableString(value);
  }
  return jsonReplacerSafeBigInt(key, value);
};

export const makeJsonReplacerElideLargeArrays =
  (limit: number = 100) =>
  (_key: string, value: unknown): unknown => {
    if (Array.isArray(value) && value.length > limit) {
      return [
        ...value.slice(0, limit),
        `+ ${value.length - limit} more values (download to view)...`,
      ];
    }

    return value;
  };

type JsonValueReplacer = (key: string, value: unknown) => unknown;

const isTypedArray = (value: unknown): value is ArrayLike<JsonType> => {
  return ArrayBuffer.isView(value) && !(value instanceof DataView);
};

/**
 * TODO: Handle has-many values
 * FIXME: We write down multiple values for many (non-has-many) queries and don't distinguish the actual inputs/outputs
 * that were returned to the user for the query. We should distinguish them and display them in that case.
 */
export const formatArbitraryJsonValue = (
  value: unknown,
  prettyFormatNumbers: boolean = false,
  space: number | undefined = 2,
  extraReplacer?: JsonValueReplacer,
) => {
  if (isMessage(value, TimestampSchema)) {
    return timestampToDate(value).toISOString();
  }

  const jsonReplacer = (_key: string, value: unknown): unknown => {
    if (value == null) {
      return null;
    } else if (value instanceof Uint8Array) {
      return prettyFormatByteArray(value);
    } else if (isTypedArray(value)) {
      return Array.from(value);
    } else if (typeof value === "number") {
      return prettyFormatNumbers ? formatInt(value) : value;
    } else if (typeof value === "bigint") {
      if (value <= BigInt(Number.MAX_SAFE_INTEGER) && value >= BigInt(Number.MIN_SAFE_INTEGER)) {
        return Number(value);
      } else {
        return value.toString();
      }
    } else if (typeof value === "string") {
      try {
        // not comprehensive check for json, attempt parse + stringify with space
        // and fall back to original string value
        if (stringIsStringifiedJson(value)) {
          return JSON.stringify(JSON.parse(value), applyReplacer, space);
        }
      } catch {
        /* do nothing and fall back to base case, it's ok */
      }
    }
    return value;
  };

  const applyReplacer = (key: string, value: unknown): unknown => {
    const replaced = jsonReplacer(key, value);
    return extraReplacer == null ? replaced : extraReplacer(key, replaced);
  };

  // Special case primitives - these will get double-cased in quotations that look unnecessary
  if (
    value instanceof Uint8Array ||
    ["number", "string", "boolean", "bigint"].includes(typeof value)
  ) {
    return `${applyReplacer("", value)}`;
  }

  // Default - throw it into json stringify and let the json replacer recursively handle it
  return JSON.stringify(value, applyReplacer, space);
};
